import { useEffect, useMemo, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";

import * as echarts from "echarts";
import { Currency } from "shared/utils/Currency";
import ComboFilters from "../ComboFilters";
import { Numbers } from "shared/utils/Numbers";
import { formatByType } from "../Reports";

export default function CreativesInPeriod({ data = [] }) {
  const [selectedOption, setSelectedOption] = useState({
    name: "Custo com disparos",
    id: "cost",
  });

  const _data = {
    entregas: {
      series: ["Qtd. de entregas"],
      value_type: "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d.entregas || 0)],
    },
    sends: {
      series: ["Qtd. de disparos"],
      value_type: "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d.sends || 0)],
    },
    clicks: {
      series: ["Qtd. de cliques"],
      value_type: "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d.clicks || 0)],
    },
    opens: {
      series: ["Qtd. de aberturas"],
      value_type: "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d.opens || 0)],
    },
    cost: {
      series: ["Custo com disparos"],
      value_type: "currency",
      colors: ["#f288a4"],
      data: [data.map((d) => d.cost || 0)],
    },
  };

  const totalData = [];

  const getDataByIndex = (index) => {
    return data[index];
  };

  const memoData = useMemo(() => data, [JSON.stringify(data)]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const chartDOM = document.getElementById("creatives-in-period-chart");
      const chart = echarts.init(chartDOM, null, {
        renderer: "svg",
      });
      const selectedOptionId = selectedOption.id;

      const options = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "none",
          },
          backgroundColor: "#2B2B2B",
          borderRadius: 8,
          padding: [8, 8],
          borderWidth: 0,
          textStyle: {
            color: "#FFF",
            fontSize: 10,
          },
          formatter: (params) => {
            const index = params[0].dataIndex;
            const item = getDataByIndex(index);

            return `
              <div>
                <div style="line-height:20px">
                  <span style="color:#fff;opacity:50%">Custo com disparos:</span>
                  <span style="color:#fff">${Currency.formatCurrency(
                    +item.cost
                  )}</span>
                </div>
                <div style="line-height:20px">
                  <span style="color:#fff;opacity:50%">Qtd. de disparos:</span>
                  <span style="color:#fff">${Numbers.formatInt(
                    item.sends
                  )}</span>
                </div>
                <div style="line-height:20px">
                  <span style="color:#fff;opacity:50%">Qtd. de disparos:</span>
                  <span style="color:#fff">Em breve</span>
                </div>
                <div style="line-height:20px">
                  <span style="color:#fff;opacity:50%">Qtd. de aberturas:</span>
                  <span style="color:#fff">${Numbers.formatInt(
                    item.opens
                  )}</span>
                </div>
                <div style="line-height:20px">
                  <span style="color:#fff;opacity:50%">Qtd. de cliques:</span>
                  <span style="color:#fff">${Numbers.formatInt(
                    item.clicks
                  )}</span>
                </div>
              </div>
            `;
          },
        },
        grid: {
          left: 0,
          right: 10,
          top: 30,
          bottom: 0,
          containLabel: true,
        },
        xAxis: [
          {
            type: "category",
            data: data.map((d) => d.date),
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              color: "rgba(217, 217, 217, 0.5)",
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            axisLine: {
              lineStyle: {
                color: "rgba(217, 217, 217, 0.5)",
              },
            },
            splitLine: {
              lineStyle: {
                color: "rgba(51, 51, 51, 0.55)",
                width: 1,
                type: "solid",
              },
            },
            axisLabel: {
              formatter: (value) =>
                ["cost", "influenced", "total_with_discounts"].includes(
                  selectedOption.id
                )
                  ? Currency.formatCurrency(value)
                  : Numbers.formatInt(value),
              color: "rgba(217, 217, 217, 0.5)",
            },
          },
        ],
        series: _data[selectedOptionId].series.map((name, sid, series) => {
          return {
            name,
            type: "bar",
            stack: "total",
            barWidth: "80%",
            data: _data[selectedOptionId].data[sid].map((d, did) =>
              totalData[did] <= 0 ? 0 : d
            ),
            label: {
              show: sid == series.length - 1,
              position: "top",
              lineHeight: 20,
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: 10,
              fontWeight: "semibold",
              formatter: (params) => {
                const index = params.dataIndex;
                const total = _data[selectedOptionId].data[sid][index];
                if (total <= 0) return "";
                return formatByType(total, _data[selectedOptionId].value_type);
              },
            },
            itemStyle: {
              color: _data[selectedOptionId].colors[sid],
            },
          };
        }),
      };

      options && chart.setOption(options);

      const ro = new ResizeObserver(() => {
        chart.resize();
      });
      ro.observe(chartDOM);

      return () => {
        ro.unobserve(chartDOM);
        ro.disconnect();
        chart.dispose();
      };
    }
  }, [memoData, selectedOption]);

  return (
    <div className={styles.box}>
      <div className="d-flex align-center justify-between mb-20">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Criativos no período</p>
          <span className={styles.info}>
            <i className="fal fa-info-circle" />
          </span>
        </div>
        <ComboFilters
          options={[
            { name: "Custo com disparos", id: "cost" },
            { name: "Qtd. de disparos", id: "sends" },
            { name: "Qtd. de entregas", id: "entregas" },
            { name: "Qtd. de aberturas", id: "opens" },
            { name: "Qtd. de cliques", id: "clicks" },
          ]}
          onSelect={(option) => setSelectedOption(option)}
          selectedOption={
            selectedOption || {
              name: "Custo com disparos",
              id: "cost",
            }
          }
        />
      </div>

      <div className={styles.chart}>
        {_data[selectedOption.id].data[0].every(
          (s) => s === 0 || s === null
        ) ? (
          <div className={styles.emptyChart} style={{ height: 350 }}>
            <span className="text-grey">Não há dados neste período</span>
          </div>
        ) : null}
        <div
          id="creatives-in-period-chart"
          style={{ width: "100%", height: 350 }}
        />
      </div>
    </div>
  );
}
